import { DEFAULT_COUNT_GOOD, NUMBER_REGEX } from '@/constants/constants';
import {
    ERROR_REMOVE_BUCKET_GOOD,
    ERROR_UPLOAD_BUCKET_GOOD,
    SUCCESS_REMOVE_BUCKET_GOOD,
    SUCCESS_UPLOAD_BUCKET_GOOD,
} from '@/constants/labels';
import { StorageKey } from '@/enums/local-storage/storage-key.enum';
import { GoodData } from '@/models/client-orders/good-data.model';
import { ExcludeStringIncomingEntity } from '@/models/entities/exclude-str-incoming-ent.model';
import { GoodType } from '@/models/entities/good-type.model';
import { IUserDeliveryAddress } from '@/models/store/client-orders/delivery-addresses/delivery-address.model';
import { IOrder } from '@/models/store/client-orders/orders/order.model';
import { UpdateOrder } from '@/models/store/client-orders/orders/update-order.model';
import router from '@/router';
import { storageService } from '@/services/storage';
import { store } from '@/store';
import { isEqual } from 'lodash';

export const createCurrentGood = (
    goodType: GoodType,
    data: ExcludeStringIncomingEntity,
): GoodData => {
    const count = DEFAULT_COUNT_GOOD;
    const {
        typeName,
        name,
        brand,
        size,
        season,
        index,
        salePrice,
        vendorCode,
        volume,
        startingCurrent,
        external,
        internal,
    } = data;
    const commonData = { goodType, count, typeName, itemPrice: Number(salePrice) };

    switch (goodType) {
        case 'tires':
            return { name, brand, size, season, index, ...commonData };
        case 'cargo':
            return { goodType: 'cargo', typeName, name, count, itemPrice: salePrice };
        case 'autoCase':
            return { vendorCode, ...commonData };
        case 'batteries':
            return { name, brand, volume, startingCurrent, ...commonData };
        case 'rings':
            return { external, internal, ...commonData };
        case 'mat':
            return { vendorCode, ...commonData };
        case 'screw':
            return { name, ...commonData };
        case 'wheels':
            return { vendorCode, ...commonData };
        default:
            return { goodType: 'tires', count, typeName: '', vendorCode: '', itemPrice: 0 };
    }
};

export const orderService = {
    fetchOrders: (): void => {
        store.dispatch('getClientOrders', {});
    },
    placeOrderInBucket: async (
        goodType: GoodType,
        data: ExcludeStringIncomingEntity,
    ): Promise<boolean> => {
        let order!: IOrder;
        const bucketOrder = store.getters.getBucketOrders.at(0);
        const currentGood = createCurrentGood(goodType, data);

        if (!Number(String(data.rest).replace(NUMBER_REGEX, ''))) {
            store.commit('setSnackbarState', {
                status: true,
                text: 'Возможно товара нет в наличии, свяжитесь с администратором',
                isError: true,
            });

            return false;
        }

        if (bucketOrder) {
            bucketOrder.goods.push(currentGood);
            order = await store.dispatch('updateOrder', bucketOrder);
        } else {
            const defaultOrder = store.getters.getDefaultOrder;
            defaultOrder.goods.push(currentGood);
            order = await store.dispatch('createOrder', defaultOrder);
        }

        store.dispatch('changeSnackbar', {
            status: true,
            text: order ? SUCCESS_UPLOAD_BUCKET_GOOD : ERROR_UPLOAD_BUCKET_GOOD,
            isError: !!order,
        });

        if (order) storageService.setLocalStorageData(StorageKey.CLIENT, order?.userId);

        return !!order;
    },

    updateOrder: async (
        data: UpdateOrder | unknown,
        isFormUpdate: boolean,
    ): Promise<IOrder | undefined> => {
        const order = await store.dispatch('updateOrder', data);

        if (!isFormUpdate)
            store.dispatch('changeSnackbar', {
                status: true,
                text: order ? SUCCESS_REMOVE_BUCKET_GOOD : ERROR_REMOVE_BUCKET_GOOD,
                isError: !!order,
            });

        return order;
    },

    upsertUserDeliveryAddress: async (
        data: IUserDeliveryAddress & { isCreate: boolean },
    ): Promise<IUserDeliveryAddress> => {
        const { userId, city, street, building, phone, isCreate } = data;
        const addresses = await store.dispatch('getUserDeliveryAddressById', { userId });
        const candidate = addresses.at(0);

        if (!candidate || isCreate) return await store.dispatch('createDeliveryAddress', data);

        if (
            isEqual(candidate.city, city) &&
            isEqual(candidate.street, street) &&
            isEqual(candidate.building, building) &&
            isEqual(candidate.phone, phone)
        )
            return candidate;
        else
            return await store.dispatch('updateDeliveryAddress', {
                _id: candidate._id,
                city,
                street,
                building,
                phone,
            });
    },

    logout(): void {
        storageService.removeLocalStorageData(StorageKey.TOKEN);
        storageService.removeLocalStorageData(StorageKey.CLIENT);
        orderService.fetchOrders();
        store.commit('setToken', '');
        store.commit('setUnAuthorized', true);
        router.push({ path: '/' });
    },

    fetchFormData: () => {
        const formData = store.getters.getOrderFormData?.termsOfAgreementLink;

        if (formData) return;
        else store.dispatch('fetchOrderFormData');
    },
};
